import { JSONContent } from "@tiptap/react";
import { normalizeKey } from "./reportInfoFieldAliases";

const cellText = (node: JSONContent): string =>
  node.text ?? (node.content || []).map(cellText).join(" ");

const summaryAliases = (label: string): string[] => {
  if (label.includes("visualinspection")) return ["vi_findings"];
  if (label.includes("wetleakage"))
    return ["wet_leakage_disclaimer", "Wet_leakage_insulation"];
  if (label.includes("electricalcircuitry"))
    return ["el_disclaimer", "Electrical_circuitry"];
  if (label.includes("deviation") && label.includes("bifaciality"))
    return ["average_bifaciality_deviation"];
  if (label.includes("deviation"))
    return ["deviation_percentage", "deviation"];
  if (label.includes("change") && label.includes("pmax"))
    return ["deviation", "deviation_percentage"];
  if (label.includes("measuredbifaciality"))
    return ["average_measured_bifaciality"];
  if (
    label.includes("stc") ||
    label.includes("averagepmp") ||
    label.includes("pmax")
  )
    return ["average_pmp", "P_max"];
  return [];
};

/**
 * Binds summary labels to adjacent values or the row below, without assuming
 * a fixed column count. Exact API labels take precedence over legacy aliases.
 * Returns null when the table has no recognized summary fields.
 */
export const bindSummaryTable = (
  rows: JSONContent[],
  apiItem: Record<string, unknown>,
): JSONContent[] | null => {
  const keys = Object.keys(apiItem);
  const valueForLabel = (text: string): string | undefined => {
    const label = normalizeKey(text);
    if (!label) return undefined;
    const exactKey = keys.find((key) => normalizeKey(key) === label);
    const aliases = summaryAliases(label);
    if (exactKey === undefined && !aliases.length) return undefined;
    const value =
      exactKey !== undefined
        ? apiItem[exactKey]
        : aliases
            .map((alias) => keys.find((key) => normalizeKey(key) === normalizeKey(alias)))
            .map((key) => key === undefined ? undefined : apiItem[key])
            .find((item) => item != null);
    if (value === null || value === undefined || value === "")
      return label.includes("visualinspection") ? "None" : "N/A";
    return typeof value === "number" && !Number.isInteger(value)
      ? value.toFixed(2)
      : String(value);
  };

  const labels = rows.map((row) =>
    (row.content || []).map((cell) => valueForLabel(cellText(cell))),
  );
  if (!labels.some((row) => row.some((value) => value !== undefined)))
    return null;

  const updatedRows = rows.map((row) => ({
    ...row,
    content: (row.content || []).map((cell) => ({ ...cell })),
  }));
  const setValue = (cell: JSONContent, value: string) => {
    cell.content = [
      { type: "paragraph", content: [{ type: "text", text: value }] },
    ];
  };
  const span = (cell: JSONContent): number => cell.attrs?.colspan || 1;
  const insertions = new Map<number, JSONContent>();

  rows.forEach((row, rowIndex) => {
    const cells = row.content || [];
    const rowLabels = labels[rowIndex];
    const isKeyValueRow =
      cells.length % 2 === 0 &&
      cells.every((cell, index) =>
        index % 2 === 0
          ? rowLabels[index] !== undefined
          : rowLabels[index] === undefined && cell.type !== "tableHeader",
      );

    let column = 0;
    cells.forEach((cell, cellIndex) => {
      const value = rowLabels[cellIndex];
      const labelColumn = column;
      column += span(cell);
      if (value === undefined) return;
      if (isKeyValueRow) {
        setValue(updatedRows[rowIndex].content[cellIndex + 1], value);
        return;
      }

      const nextCells = rows[rowIndex + 1]?.content || [];
      let nextColumn = 0;
      const targetIndex = nextCells.findIndex((nextCell) => {
        // A merged section heading spans both the model and value columns.
        // Bind to the value at its right edge, not the model at its left edge.
        const matches =
          nextColumn >= labelColumn &&
          nextColumn + span(nextCell) === labelColumn + span(cell);
        nextColumn += span(nextCell);
        return matches;
      });
      // Never replace another label when a value row is missing.
      if (
        targetIndex !== -1 &&
        !labels[rowIndex + 1].some((label) => label !== undefined)
      ) {
        setValue(updatedRows[rowIndex + 1].content[targetIndex], value);
      } else {
        let valueRow = insertions.get(rowIndex);
        if (!valueRow) {
          valueRow = {
            type: "tableRow",
            content: cells.map((header) => ({
              type: "tableCell",
              attrs: { ...header.attrs, rowspan: 1 },
              content: [{ type: "paragraph" }],
            })),
          };
          insertions.set(rowIndex, valueRow);
        }
        const target = valueRow.content?.[cellIndex];
        if (target) setValue(target, value);
      }
    });
  });

  return updatedRows.flatMap((row, index) => {
    const inserted = insertions.get(index);
    return inserted ? [row, inserted] : [row];
  });
};
